import { useMemo } from "react"
import { Locker } from "shared/locker"
import { Sstv2Migration } from "shared/sstv2Migration"
import { Tenant } from "shared/tenant"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"
import { TenantMigrationData } from "./TenantMigrationAccordion"
import { FleetTotals } from "./Sstv2FleetRollup"
import {
  Sstv2MigrationMetricCounts,
  Sstv2MigrationRow,
  computeStall,
  isRollbackPhase,
} from "./sstv2MigrationTypes"

/**
 * Derive every view-model the migration page renders from the three raw Redux
 * lists (lockers, tenants, migration entities). Extracted from the page so the
 * page component stays a thin layout shell (< 400 lines per repo rule).
 */
export const useSstv2MigrationView = (
  lockers: Locker[],
  tenants: Tenant[],
  migrations: Sstv2Migration[],
  /** Reference "now" (ms) — drives the stalled count. Ticks under polling. */
  nowMs: number,
): {
  migrationData: TenantMigrationData[]
  totals: FleetTotals
  migrationRows: Sstv2MigrationRow[]
  migrationCounts: Sstv2MigrationMetricCounts
} => {
  // Per-tenant fleet rollup (V1 vs V2 from locker.sstApiVersion).
  const migrationData = useMemo<TenantMigrationData[]>(() => {
    const grouped = new Map<string, Locker[]>()
    for (const locker of lockers) {
      const tid = locker.tenantId || "unknown"
      if (!grouped.has(tid)) grouped.set(tid, [])
      grouped.get(tid)!.push(locker)
    }

    const tenantData: TenantMigrationData[] = []
    for (const [tenantId, tenantLockers] of grouped) {
      const tenant = tenants.find((t) => t.id === tenantId)
      const v1 = tenantLockers.filter(
        (l) => !l.sstApiVersion || l.sstApiVersion === "v1",
      ).length
      const v2 = tenantLockers.filter((l) => l.sstApiVersion === "v2").length
      tenantData.push({
        tenantId,
        tenantName: tenant?.name || tenantId,
        v1Count: v1,
        v2Count: v2,
        totalCount: tenantLockers.length,
        v2Percentage:
          tenantLockers.length > 0 ? (v2 / tenantLockers.length) * 100 : 0,
        lockers: tenantLockers,
      })
    }
    return tenantData
  }, [lockers, tenants])

  const totals = useMemo<FleetTotals>(() => {
    const v1 = lockers.filter(
      (l) => !l.sstApiVersion || l.sstApiVersion === "v1",
    ).length
    const v2 = lockers.filter((l) => l.sstApiVersion === "v2").length
    const total = lockers.length
    return { v1, v2, total, v2Percentage: total > 0 ? (v2 / total) * 100 : 0 }
  }, [lockers])

  // One row per migration JOB (attempt), joined with locker + tenant names.
  const migrationRows = useMemo<Sstv2MigrationRow[]>(() => {
    const tenantName = (tenantId: string) =>
      tenants.find((t) => t.id === tenantId)?.name || tenantId
    const lockerNameBySstId = (sstId?: string): string => {
      if (!sstId) return "-"
      const locker = lockers.find((l) => l.sstLockerId === sstId)
      return locker ? getLockerDisplayValues(locker).i18n.name : sstId
    }

    // Attempt numbering per (tenant, locker), oldest first — so a row can say
    // "attempt 3 of 4" instead of leaving the reader to guess why one locker
    // appears several times.
    const startedAtOf = (m: Sstv2Migration): string => {
      const stamps = (m.events ?? []).map((e) => e.timestamp).filter(Boolean)
      return stamps.length > 0 ? stamps.reduce((a, b) => (a < b ? a : b)) : m.lastEventAt
    }
    const attemptsByLocker = new Map<string, Sstv2Migration[]>()
    for (const m of migrations) {
      const key = `${m.tenantId}/${m.sl1LockerId}`
      if (!attemptsByLocker.has(key)) attemptsByLocker.set(key, [])
      attemptsByLocker.get(key)!.push(m)
    }
    for (const list of attemptsByLocker.values()) {
      list.sort((a, b) => startedAtOf(a).localeCompare(startedAtOf(b)))
    }
    const attemptIndexOf = (m: Sstv2Migration): number =>
      (attemptsByLocker.get(`${m.tenantId}/${m.sl1LockerId}`) ?? []).findIndex(
        (x) => x.id === m.id,
      ) + 1

    return migrations.map((m) => ({
      id: m.id,
      tenantId: m.tenantId,
      tenantName: tenantName(m.tenantId),
      migrationId: m.migrationId,
      sl1LockerId: m.sl1LockerId,
      v1LockerName: lockerNameBySstId(m.sl1LockerId),
      v2LockerName: lockerNameBySstId(m.migrationId),
      attemptCount: attemptsByLocker.get(`${m.tenantId}/${m.sl1LockerId}`)?.length ?? 1,
      attemptIndex: attemptIndexOf(m),
      startedAt: startedAtOf(m),
      phase: m.phase,
      isRollback: isRollbackPhase(m.phase),
      lastEventType: m.lastEventType,
      lastEventAt: m.lastEventAt,
      createdSl2LockerThingId: m.createdSl2LockerThingId,
      processingState: m.processingState,
      activeReceived: m.activeReceived,
      activeProcessed: m.activeProcessed,
      activeFailed: m.activeFailed,
      activeAdopted: m.activeAdopted,
      historyReceived: m.historyReceived,
      historyProcessed: m.historyProcessed,
      historyFailed: m.historyFailed,
      historyAdopted: m.historyAdopted,
      // FOLD PROVENANCE. This map is field-by-field, so a stamp not listed here is
      // silently `undefined` downstream — and `undefined` means UNPROVEN, so omitting
      // these would make every stream read "not yet certified" for ever. A dropped
      // field here fails as a confident wrong answer, not as a blank.
      activeFoldedCount: m.activeFoldedCount,
      historyFoldedCount: m.historyFoldedCount,
      partnerFailed: m.partnerFailed,
      operationalEventReceived: m.operationalEventReceived,
      activeDiscrepancy: m.activeDiscrepancy,
      completedWithFailures: m.completedWithFailures,
      activeMaterializationEmpty: m.activeMaterializationEmpty,
      source: m,
    }))
  }, [migrations, lockers, tenants])

  /**
   * Counts describe JOBS (attempts), except `lockersLive` which deliberately
   * counts LOCKERS: with one row per attempt, "% of rows cut over" is a
   * meaningless ratio (rolled-back attempts are rows too), while "how many
   * lockers are actually live on V2" is the number an operator acts on. Fleet-wide
   * V1-vs-V2 percentages live in the fleet rollup, not here.
   */
  const migrationCounts = useMemo<Sstv2MigrationMetricCounts>(() => {
    let inFlight = 0
    let completed = 0
    let rolledBack = 0
    let stalled = 0
    /** Latest attempt per locker — decides whether that locker is live on V2. */
    const latestByLocker = new Map<string, Sstv2MigrationRow>()

    for (const r of migrationRows) {
      if (r.phase === "COMPLETED") completed += 1
      else if (r.isRollback) rolledBack += 1
      else inFlight += 1

      if (computeStall(r.phase, r.lastEventAt, nowMs).isStalled) stalled += 1

      const key = `${r.tenantId}/${r.sl1LockerId}`
      const prev = latestByLocker.get(key)
      if (!prev || prev.startedAt < r.startedAt) latestByLocker.set(key, r)
    }

    let lockersLive = 0
    for (const r of latestByLocker.values()) {
      if (r.phase === "OPERATIONAL" || r.phase === "COMPLETED") lockersLive += 1
    }

    return {
      jobs: migrationRows.length,
      lockers: latestByLocker.size,
      inFlight,
      completed,
      rolledBack,
      stalled,
      lockersLive,
    }
  }, [migrationRows, nowMs])

  return { migrationData, totals, migrationRows, migrationCounts }
}
